import { useId, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import type { SelectProps, SelectOption } from './types';
import { computeSelectDropPlacement, type DropPlacement } from './drop-direction';

// ─── Inline spinner (matches Input/Button pattern) ────────────────────────────

function SelectSpinner() {
  return (
    <span
      className="inline-block animate-spin text-neutral-400 dark:text-neutral-500 shrink-0"
      role="status"
      aria-label="Loading"
    >
      <svg
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
    </span>
  );
}

// ─── Chevron icon ─────────────────────────────────────────────────────────────

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={[
        'h-4 w-4 text-neutral-400 shrink-0 transition-transform duration-150',
        open ? 'rotate-180' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Clear button icon ────────────────────────────────────────────────────────

function ClearIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M18 6 6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Walk up the DOM to find the nearest ancestor that would clip an
// absolutely-positioned descendant. Stops at <body>.
function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'hidden' ||
      overflowX === 'auto' ||
      overflowX === 'scroll' ||
      overflowX === 'hidden'
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

// ─── Select ───────────────────────────────────────────────────────────────────

export function Select({
  options,
  value,
  onChange,
  variant = 'default',
  label,
  placeholder = 'Select...',
  error,
  disabled = false,
  required = false,
  loading = false,
  clearable = false,
  maxItems,
  onSearch,
  id: idProp,
  className,
}: SelectProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const errorId = error ? `${id}-error` : undefined;
  const listboxId = `${id}-listbox`;
  const searchId = `${id}-search`;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [dropPlacement, setDropPlacement] = useState<DropPlacement>({
    direction: 'down',
    maxHeight: 240,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRowRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const isMulti = variant === 'multi-select';
  const isSearchable = variant === 'searchable' || isMulti;

  // Normalise value to arrays internally
  const selectedValues: string[] = isMulti
    ? Array.isArray(value)
      ? value
      : value
        ? [value as string]
        : []
    : Array.isArray(value)
      ? value.slice(0, 1)
      : value
        ? [value as string]
        : [];

  const hasValue = selectedValues.length > 0;

  // ─── Filter options ────────────────────────────────────────────────────────

  const filteredOptions: SelectOption[] =
    isSearchable && search && !onSearch
      ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
      : options;

  // ─── Close on outside click ────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
        setFocusedIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open && isSearchable) {
      searchRef.current?.focus();
    }
  }, [open, isSearchable]);

  // Flip the dropdown above the trigger when there isn't enough room below
  // (regression #83 — Add Table modal clipped the Shape dropdown options).
  // We consider the nearest scrollable ancestor's clip rect because an
  // absolutely-positioned dropdown is clipped by any ancestor with
  // overflow:auto|scroll|hidden (e.g. the Modal body), not just the viewport.
  useLayoutEffect(() => {
    if (!open) return;
    const row = triggerRowRef.current;
    if (!row) return;
    const rect = row.getBoundingClientRect();
    const scrollParent = findScrollableAncestor(row);
    const containerRect = scrollParent?.getBoundingClientRect();
    setDropPlacement(
      computeSelectDropPlacement({
        triggerTop: rect.top,
        triggerBottom: rect.bottom,
        viewportHeight: window.innerHeight,
        containerTop: containerRect?.top,
        containerBottom: containerRect?.bottom,
      }),
    );
  }, [open]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const openDropdown = useCallback(() => {
    if (!disabled && !loading) {
      setOpen(true);
      setFocusedIndex(-1);
    }
  }, [disabled, loading]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setSearch('');
    setFocusedIndex(-1);
    triggerRef.current?.focus();
  }, []);

  const selectOption = useCallback(
    (optionValue: string) => {
      if (isMulti) {
        if (selectedValues.includes(optionValue)) {
          onChange(selectedValues.filter((v) => v !== optionValue));
        } else {
          if (maxItems !== undefined && selectedValues.length >= maxItems) return;
          onChange([...selectedValues, optionValue]);
        }
        // Keep dropdown open for multi-select
        searchRef.current?.focus();
      } else {
        onChange(optionValue);
        closeDropdown();
      }
    },
    [isMulti, selectedValues, onChange, maxItems, closeDropdown],
  );

  const clearSelection = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(isMulti ? [] : '');
    },
    [isMulti, onChange],
  );

  const removeTag = useCallback(
    (val: string, e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(selectedValues.filter((v) => v !== val));
    },
    [selectedValues, onChange],
  );

  // ─── Keyboard navigation ───────────────────────────────────────────────────

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    // A Space typed in the search input bubbles here — it is text, not "open".
    if (e.key === ' ' && e.target === searchRef.current) return;
    // Escape on the focused trigger closes the open list and nothing else.
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      closeDropdown();
      return;
    }
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      openDropdown();
    }
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    // Escape closes the open list and nothing else: a page's own Escape
    // shortcut (e.g. "back to the list") must not also see this key.
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      closeDropdown();
      return;
    }
    const enabledIndices = filteredOptions.reduce<number[]>((acc, opt, i) => {
      if (!opt.disabled) acc.push(i);
      return acc;
    }, []);
    if (enabledIndices.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const currentPos = enabledIndices.indexOf(focusedIndex);
      const next = enabledIndices[(currentPos + 1) % enabledIndices.length];
      setFocusedIndex(next);
      scrollOptionIntoView(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const currentPos = enabledIndices.indexOf(focusedIndex);
      const prev =
        enabledIndices[(currentPos - 1 + enabledIndices.length) % enabledIndices.length];
      setFocusedIndex(prev);
      scrollOptionIntoView(prev);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
        selectOption(filteredOptions[focusedIndex].value);
      }
    } else if (e.key === 'Tab') {
      closeDropdown();
    } else if (e.key === 'Backspace' && isMulti && search === '') {
      // Remove last tag on Backspace
      if (selectedValues.length > 0) {
        onChange(selectedValues.slice(0, -1));
      }
    }
  };

  const scrollOptionIntoView = (index: number) => {
    const list = listRef.current;
    if (!list) return;
    const item = list.children[index] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  };

  // ─── Derived display ───────────────────────────────────────────────────────

  const selectedLabels = selectedValues.map(
    (v) => options.find((o) => o.value === v)?.label ?? v,
  );

  // ─── Trigger display content ───────────────────────────────────────────────

  const hasError = Boolean(error);

  const triggerRowClasses = [
    'flex items-center w-full border rounded bg-white overflow-hidden transition-colors duration-150 min-h-[44px] sm:min-h-[2.75rem]',
    'dark:bg-neutral-800 dark:border-neutral-600 dark:text-neutral-100',
    hasError
      ? 'border-danger-500 ring-2 ring-danger-500 ring-offset-0'
      : open
        ? 'border-brand-500 ring-2 ring-brand-500 ring-offset-0'
        : 'border-neutral-300 hover:border-neutral-400',
    disabled || loading
      ? 'bg-neutral-100 dark:bg-neutral-700 cursor-not-allowed opacity-70 pointer-events-none'
      : 'cursor-pointer',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={containerRef}
      className={[
        'flex flex-col gap-1 relative',
        // When the dropdown is open, lift the whole Select into its own
        // high-z stacking context so the absolutely-positioned listbox can
        // float above sibling cards/form sections that create their own
        // stacking contexts (e.g. backdrop-blur panels). Regression #98 —
        // Quick-Add Inventory Adjustment Type options were clipped by the
        // Quantity card below.
        open ? 'z-50' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Label */}
      {label && (
        <label
          htmlFor={isSearchable ? searchId : id}
          className="text-sm font-medium text-neutral-700 dark:text-neutral-300"
        >
          {label}
          {required && (
            <span className="text-danger-500 ml-1" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      {/* Trigger */}
      <div
        ref={triggerRowRef}
        className={triggerRowClasses}
        onClick={openDropdown}
        onKeyDown={handleTriggerKeyDown}
      >
        {/* Multi-select tags + search input area, or plain trigger button */}
        {isMulti ? (
          <div className="flex-1 flex flex-wrap items-center gap-1 px-3 py-2 min-w-0">
            {selectedValues.map((val, i) => (
              <span
                key={val}
                className="inline-flex items-center gap-1 bg-brand-100 dark:bg-brand-900 text-brand-700 dark:text-brand-300 text-xs font-medium px-2 py-0.5 rounded"
              >
                {selectedLabels[i]}
                <button
                  type="button"
                  className="text-brand-500 hover:text-brand-700 dark:hover:text-brand-200 focus:outline-none"
                  onClick={(e) => removeTag(val, e)}
                  aria-label={`Remove ${selectedLabels[i]}`}
                  tabIndex={-1}
                >
                  <ClearIcon />
                </button>
              </span>
            ))}
            <input
              ref={searchRef}
              id={searchId}
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                onSearch?.(e.target.value);
                if (!open) setOpen(true);
              }}
              onKeyDown={handleListKeyDown}
              placeholder={selectedValues.length === 0 ? placeholder : ''}
              disabled={disabled}
              aria-autocomplete="list"
              aria-controls={listboxId}
              aria-expanded={open}
              aria-required={required || undefined}
              aria-invalid={hasError || undefined}
              aria-describedby={errorId}
              className="flex-1 min-w-[60px] bg-transparent outline-none border-none text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500"
            />
          </div>
        ) : isSearchable ? (
          <div className="flex-1 flex items-center px-3 py-2 min-w-0">
            <input
              ref={searchRef}
              id={searchId}
              type="text"
              value={open ? search : (selectedLabels[0] ?? '')}
              onChange={(e) => {
                setSearch(e.target.value);
                onSearch?.(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={openDropdown}
              onKeyDown={handleListKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              aria-autocomplete="list"
              aria-controls={listboxId}
              aria-expanded={open}
              aria-required={required || undefined}
              aria-invalid={hasError || undefined}
              aria-describedby={errorId}
              className="flex-1 bg-transparent outline-none border-none text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 min-w-0 cursor-pointer"
              readOnly={!open}
            />
          </div>
        ) : (
          <button
            ref={triggerRef}
            id={id}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-required={required || undefined}
            aria-invalid={hasError || undefined}
            aria-describedby={errorId}
            className="flex-1 flex items-center px-3 py-2 text-sm text-left bg-transparent outline-none border-none min-w-0"
          >
            <span
              className={[
                'truncate',
                hasValue
                  ? 'text-neutral-900 dark:text-neutral-100'
                  : 'text-neutral-400 dark:text-neutral-500',
              ].join(' ')}
            >
              {hasValue ? selectedLabels[0] : placeholder}
            </span>
          </button>
        )}

        {/* Right side: clear button + spinner/chevron */}
        <div className="flex items-center gap-1 pr-3 shrink-0">
          {clearable && hasValue && !loading && (
            <button
              type="button"
              className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 focus:outline-none p-0.5"
              onClick={clearSelection}
              aria-label="Clear selection"
              tabIndex={-1}
            >
              <ClearIcon />
            </button>
          )}
          {loading ? <SelectSpinner /> : <ChevronIcon open={open} />}
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-multiselectable={isMulti}
          aria-label={label}
          onKeyDown={!isSearchable ? handleListKeyDown : undefined}
          tabIndex={!isSearchable ? -1 : undefined}
          className={[
            'absolute z-50 w-full bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded shadow-lg overflow-y-auto focus:outline-none',
            dropPlacement.direction === 'up' ? 'bottom-full mb-1' : 'top-full mt-1',
          ].join(' ')}
          // Capped to the space actually available inside the clip rect, so a
          // short modal scrolls the list instead of hiding it.
          style={{ maxHeight: dropPlacement.maxHeight }}
        >
          {filteredOptions.length === 0 ? (
            <li className="px-3 py-2 text-sm text-neutral-400 dark:text-neutral-500 select-none">
              No options
            </li>
          ) : (
            filteredOptions.map((option, index) => {
              const isSelected = selectedValues.includes(option.value);
              const isFocused = focusedIndex === index;
              const isAtMax =
                isMulti && maxItems !== undefined && selectedValues.length >= maxItems;
              const isDisabled = option.disabled || (isAtMax && !isSelected);

              return (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isDisabled || undefined}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent blur on trigger/search
                    if (!isDisabled) selectOption(option.value);
                  }}
                  onMouseEnter={() => setFocusedIndex(index)}
                  className={[
                    'flex items-center justify-between px-3 py-2 text-sm cursor-pointer select-none transition-colors duration-100',
                    isDisabled
                      ? 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
                      : isFocused
                        ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300'
                        : isSelected
                          ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300'
                          : 'text-neutral-900 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-700',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && (
                    <svg
                      className="h-4 w-4 text-brand-500 shrink-0 ml-2"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </li>
              );
            })
          )}
        </ul>
      )}

      {/* Error */}
      {error && (
        <span id={errorId} role="alert" className="text-xs text-danger-600 dark:text-danger-400">
          {error}
        </span>
      )}
    </div>
  );
}
